//import '@babylonjs/core/Debug/debugLayer';
//import '@babylonjs/inspector';
import '@babylonjs/loaders/glTF/2.0';

import {
  Engine,
  Animation,
  Animatable,
  ImportMeshAsync,
  Scene,
  Vector3,
  ArcRotateCamera,
  Color4,
  AbstractMesh,
  Mesh,
  DirectionalLight,
  HemisphericLight,
  Color3,
  ShadowGenerator,
  HighlightLayer,
} from '@babylonjs/core';
import Body from './components/Body';
import BodyPart from './components/BodyPart';

//TODO: Use the local 3D file automatically when running locally
//import gltfModel from './assets/3D/male.glb?url';
const gltfModel = 'https://resources.ilteducation.com/3DBody/male_20260324.glb';

export class App {
  public engine: Engine;
  public scene!: Scene;

  private maleMesh!: AbstractMesh;
  // Not used yet, but will be needed when we add the option to switch
  private femaleMesh!: AbstractMesh;
  private camera!: ArcRotateCamera;
  private animFrameRate: number = 60;
  private isAnimating: boolean = false;

  private maleBody!: Body;

  private currentSelectedBodyPart: BodyPart | null = null;

  private hoveredBodyPart: BodyPart | null = null;
  private hoverHighlightLayer: HighlightLayer | null = null;
  private selectHighlightLayer: HighlightLayer | null = null;

  private initialCameraPosition!: Vector3;
  private initialCameraTarget!: Vector3;
  private initialCameraRadius!: number;

  private cameraPositionOnPointerDown!: Vector3;
  private cameraPositionOnPointerUp!: Vector3;

  private readonly selectedBodyPartCallback: (part: string) => void;
  private readonly closeInfoPanelCallback: () => void;

  constructor(
    readonly canvas: HTMLCanvasElement,
    selectedBodyPartCallback: (part: string) => void,
    closeInfoPanelCallback: () => void,
  ) {
    this.selectedBodyPartCallback = selectedBodyPartCallback;
    // This callback is needed in case the user clicks on an empty area in the 3D space, to emit to the parent component that the info panel should be closed.
    // The info panel can also be closed from the Vue component
    this.closeInfoPanelCallback = closeInfoPanelCallback;
    this.engine = new Engine(canvas, true, {
      preserveDrawingBuffer: true,
      stencil: true,
    });
    //this.engine.setHardwareScalingLevel(1 / window.devicePixelRatio);

    window.addEventListener('resize', this.onResize.bind(this));
  }

  async initScene() {
    await this.createScene(this.engine, this.canvas);
    if (!this.hoverHighlightLayer) {
      this.hoverHighlightLayer = new HighlightLayer('hoverHL', this.scene);
    }
    if (!this.selectHighlightLayer) {
      this.selectHighlightLayer = new HighlightLayer('selectedHL', this.scene);
    }
    this.run();
  }

  debug() {
    //**for development: make inspector visible/invisible
    window.addEventListener('keydown', (ev) => {
      if (ev.key === 'd') {
        if (this.scene.debugLayer.isVisible()) {
          this.scene.debugLayer.hide();
        } else {
          this.scene.debugLayer.show();
        }
      }
    });
  }

  run() {
    // TODO: Remove in production, but useful to have the option to quickly toggle the inspector when developing or testing.
    //this.debug();
    this.engine.runRenderLoop(() => {
      this.scene.render();
    });
  }

  private async createScene(engine: Engine, canvas: HTMLCanvasElement) {
    // This creates a basic Babylon Scene object (non-mesh)
    this.scene = new Scene(engine);
    this.scene.clearColor = new Color4(0.129, 0.161, 0.22, 1);
    this.scene.onPointerDown = this.onScenePointerDown.bind(this);
    this.scene.onPointerUp = this.onScenePointerUp.bind(this);

    const light = new DirectionalLight('DirectionalLight', new Vector3(0, -1, 0), this.scene);
    light.intensity = 2;
    light.autoUpdateExtends = false;
    light.shadowMinZ = 0.5;
    light.shadowMaxZ = 1.5;
    light.orthoLeft = -0.15;
    light.orthoRight = 0.2;
    light.orthoBottom = -0.15;
    light.orthoTop = 0.4;

    const ambientSkyLight = new HemisphericLight('HemiLight', new Vector3(0, 1, 0), this.scene);
    ambientSkyLight.intensity = 1;
    ambientSkyLight.groundColor = new Color3(0.1, 0.1, 0.2);

    const shadowGenerator = new ShadowGenerator(2048, light);

    if (shadowGenerator) {
      shadowGenerator.bias = 0.035;
      shadowGenerator.usePercentageCloserFiltering = true;
      shadowGenerator.filteringQuality = ShadowGenerator.QUALITY_MEDIUM;
    }

    const model = await ImportMeshAsync(gltfModel, this.scene);
    this.maleMesh = model.meshes[0];
    const transformNodes = this.maleMesh.getChildTransformNodes(false, (node) => !(node instanceof AbstractMesh));
    this.maleBody = new Body(transformNodes);

    this.scene.materials.forEach((material) => {
      if ((material as any).ambientColor) {
        (material as any).ambientColor.set(1, 1, 1);
      }
    });

    // This creates and positions a free camera (non-mesh)
    // Set camera target to skeleton center + offsetY
    const skeletonCenter = this.maleMesh.getBoundingInfo().boundingBox.centerWorld.clone();
    const offsetY = 1; // Move camera up by 1 unit
    skeletonCenter.y += offsetY;
    this.camera = new ArcRotateCamera('camera1', Math.PI / 2, Math.PI / 2, 3, skeletonCenter, this.scene);
    this.camera.lowerBetaLimit = Math.PI / 2; // Slightly above 0 to avoid gimbal lock
    this.camera.upperBetaLimit = Math.PI / 2; // Lock to this angle
    this.camera.panningAxis = new Vector3(0, 1, 0);
    this.camera.panningSensibility = 0; // Disable all panning
    this.camera.useNaturalPinchZoom = true;
    this.camera.upperRadiusLimit = 3;
    this.camera.allowUpsideDown = false;
    this.camera.lowerRadiusLimit = 0.4;
    this.camera.radius = 3;
    this.camera.minZ = 0.01;
    this.camera.maxZ = 10;
    this.camera.wheelDeltaPercentage = 0.1;

    // This attaches the camera to the canvas
    this.camera.attachControl(canvas, true);

    // Save initial camera state for reset
    this.initialCameraPosition = this.camera.position.clone();
    this.initialCameraTarget = skeletonCenter.clone();
    this.initialCameraRadius = this.camera.radius;

    this.scene.registerBeforeRender(() => {
      this.update();
    });
  }

  private update() {
    this.onPointerMove();
  }

  private async onScenePointerDown() {
    this.cameraPositionOnPointerDown = this.camera.position.clone();
  }

  private async onScenePointerUp() {
    this.cameraPositionOnPointerUp = this.camera.position.clone();
    if (this.cameraPositionOnPointerUp.subtract(this.cameraPositionOnPointerDown).length() > 0.02) {
      // Consider this a camera movement, not a click, so do not select/deselect a body part.
      return;
    }

    if (this.isAnimating) return; // Prevent clicking while animation is in progress
    const pickInfo = this.scene.pick(
      this.scene.pointerX,
      this.scene.pointerY,
      (mesh) => mesh.isVisible,
      false,
      this.camera,
    );

    if (!pickInfo.hit) {
      this.closeInfoPanelCallback();
      return;
    }

    if (pickInfo.pickedMesh) {
      const newBodyPart = this.maleBody.meshToBodyPart.get(pickInfo.pickedMesh.uniqueId) ?? null;

      if (!newBodyPart) {
        return;
      }

      // Prevent selecting the same body part twice.
      if (newBodyPart && this.currentSelectedBodyPart && newBodyPart.id === this.currentSelectedBodyPart.id) return;

      // Remove previous highlight layers
      this.selectHighlightLayer!.removeAllMeshes();
      this.hoverHighlightLayer!.removeAllMeshes();

      this.currentSelectedBodyPart = newBodyPart!;
      const meshes = Array.isArray(this.currentSelectedBodyPart.meshes)
        ? this.currentSelectedBodyPart.meshes
        : [this.currentSelectedBodyPart.meshes];
      for (const m of meshes) {
        this.applyHighlightToMesh(this.selectHighlightLayer!, m, Color3.FromHexString('#61B877'));
      }

      this.isAnimating = true;
      const anim = this.animateCameraToMesh(this.currentSelectedBodyPart, pickInfo.pickedMesh);
      await anim.waitAsync();
      this.isAnimating = false;
      this.selectedBodyPartCallback(this.currentSelectedBodyPart.label);
    }
  }

  private applyHighlightToMesh(highlightLayer: HighlightLayer, mesh: AbstractMesh, color: any) {
    if (!highlightLayer) return;
    highlightLayer.addMesh(mesh as Mesh, color);
  }

  private setHoveredBodyPart(part: BodyPart | null) {
    if (this.hoveredBodyPart?.id === part?.id) return;
    if (this.currentSelectedBodyPart && this.currentSelectedBodyPart.id === part?.id) return;
    this.hoveredBodyPart = part;
    this.applyHoverStyle();
  }

  private applyHoverStyle() {
    this.scene.defaultCursor = 'pointer';

    // Remove previous highlight layer
    this.hoverHighlightLayer!.removeAllMeshes();
    // Apply new
    if (this.hoveredBodyPart) {
      const meshes = Array.isArray(this.hoveredBodyPart.meshes)
        ? this.hoveredBodyPart.meshes
        : [this.hoveredBodyPart.meshes];
      for (const m of meshes) {
        this.applyHighlightToMesh(this.hoverHighlightLayer!, m, Color3.White());
      }
    }
  }

  private onPointerMove() {
    const pickInfo = this.scene.pick(
      this.scene.pointerX,
      this.scene.pointerY,
      (mesh) => mesh.isVisible,
      false,
      this.camera,
    );

    if (pickInfo.hit && pickInfo.pickedMesh) {
      const part = this.maleBody.meshToBodyPart.get(pickInfo.pickedMesh.uniqueId) ?? null;
      if (!part) {
        // Found mesh, but not part of the body or mesh without bodypart metadata. Clear hover state and return.
        this.scene.defaultCursor = '';
        this.setHoveredBodyPart(null);
        return;
      }
      this.setHoveredBodyPart(part);
    } else {
      this.scene.defaultCursor = '';
      this.setHoveredBodyPart(null);
    }
  }

  private getBodyPartFocus(part: BodyPart, clickedMesh: AbstractMesh) {
    if (clickedMesh.id.includes('Left') || clickedMesh.id.includes('Right')) {
      // Camera should not calculate the focus based on the whole body part, but rather the specific mesh that was clicked, to avoid zooming out too much for limbs.
      const bb = clickedMesh.getBoundingInfo().boundingBox;
      const center = bb.centerWorld.clone();
      const radius = bb.maximumWorld.subtract(bb.minimumWorld).length() * 0.5;

      return {
        center,
        targetRadius: Math.max(radius * 1.5, 0.6),
      };
    }
    const meshes = part.meshes; // AbstractMesh[]

    // If the BodyPart always has meshes, you can even drop this guard
    if (!Array.isArray(meshes)) {
      return { center: this.camera.target.clone(), targetRadius: this.camera.radius };
    }

    let min = new Vector3(Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY);
    let max = new Vector3(Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY);

    for (const m of meshes) {
      const bb = m.getBoundingInfo().boundingBox;
      min = Vector3.Minimize(min, bb.minimumWorld);
      max = Vector3.Maximize(max, bb.maximumWorld);
    }

    const center = min.add(max).scale(0.5);
    const radius = max.subtract(min).length() * 0.5;

    return {
      center,
      targetRadius: Math.max(radius * 1.5, 0.6),
    };
  }

  private animateCameraToMesh(part: BodyPart, clickedMesh: AbstractMesh): Animatable {
    const { center, targetRadius } = this.getBodyPartFocus(part, clickedMesh);

    const duration = this.animFrameRate; // 1 second

    // Animate camera target
    const targetAnim = new Animation(
      'cameraTargetAnim',
      'target',
      this.animFrameRate,
      Animation.ANIMATIONTYPE_VECTOR3,
      Animation.ANIMATIONLOOPMODE_CONSTANT,
    );
    targetAnim.setKeys([
      { frame: 0, value: this.camera.target.clone() },
      { frame: duration, value: center.subtract(new Vector3(0, 0, 0)) },
    ]);

    // Animate camera radius
    const zoomAnim = new Animation(
      'cameraRadiusAnim',
      'radius',
      this.animFrameRate,
      Animation.ANIMATIONTYPE_FLOAT,
      Animation.ANIMATIONLOOPMODE_CONSTANT,
    );
    zoomAnim.setKeys([
      { frame: 0, value: this.camera.radius },
      { frame: duration, value: targetRadius },
    ]);

    // Stop any previous animations
    this.scene.stopAnimation(this.camera);
    this.camera.animations = [];
    this.camera.animations.push(targetAnim, zoomAnim);

    return this.scene.beginAnimation(this.camera, 0, duration, false);
  }

  private animateCameraToInitial(): Animatable {
    const duration = this.animFrameRate;
    // Animate camera target
    const targetAnim = new Animation(
      'cameraTargetAnimReset',
      'target',
      this.animFrameRate,
      Animation.ANIMATIONTYPE_VECTOR3,
      Animation.ANIMATIONLOOPMODE_CONSTANT,
    );
    targetAnim.setKeys([
      { frame: 0, value: this.camera.target.clone() },
      { frame: duration, value: this.initialCameraTarget.clone() },
    ]);
    // Animate camera radius (zoom)
    const zoomAnim = new Animation(
      'cameraZoomAnimReset',
      'radius',
      this.animFrameRate,
      Animation.ANIMATIONTYPE_FLOAT,
      Animation.ANIMATIONLOOPMODE_CONSTANT,
    );

    zoomAnim.setKeys([
      { frame: 0, value: this.camera.radius },
      { frame: duration, value: this.initialCameraRadius },
    ]);
    this.scene.stopAnimation(this.camera);
    this.camera.animations = [];
    this.camera.animations.push(targetAnim, zoomAnim);

    return this.scene.beginAnimation(this.camera, 0, duration, false);
  }

  public onResize() {
    this.engine.resize();
  }

  public dispose() {
    window.removeEventListener('resize', this.onResize.bind(this));
    this.scene.dispose();
    this.engine.dispose();
  }

  public async closeInfoPanel() {
    this.currentSelectedBodyPart = null;
    this.hoverHighlightLayer!.removeAllMeshes();
    this.selectHighlightLayer!.removeAllMeshes();

    this.isAnimating = true;
    const anim = this.animateCameraToInitial();
    await anim.waitAsync();
    this.isAnimating = false;
  }
}
